<script lang="ts" setup>
import { type RouteLocationAsRelativeGeneric } from 'vue-router';

export type NavOption = {
  route: RouteLocationAsRelativeGeneric;
  label: string;
  labelShort: string;
  active: boolean;
};

const props = defineProps<{
  options: NavOption[];
}>();

const emit = defineEmits<{
  click: [option: NavOption];
}>();

const activeIdx = computed<number>(() =>
  props.options.findIndex((step) => step.active),
);

const onClick = function onOptionClick(option: NavOption) {
  emit('click', option);
};
</script>

<template>
  <ul class="step-navigation">
    <li
      v-for="(option, idx) in props.options"
      :key="option.route.name"
      class="step-navigation__item"
      :class="{
        'step-navigation__item--active': idx === activeIdx,
        'step-navigation__item--passed': idx < activeIdx,
      }"
    >
      <router-link
        class="step-navigation__link"
        :to="option.route"
        @click="onClick(option)"
      >
        <span class="step-navigation__check">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.5 4.5L6.5 11.4997L3 8"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        <span class="step-navigation__label step-navigation__label--pc">
          {{ option.label }}
        </span>
        <span class="step-navigation__label step-navigation__label--mob">
          {{ option.labelShort }}
        </span>
      </router-link>

      <div
        v-if="idx !== props.options.length - 1"
        class="step-navigation__line"
      ></div>
    </li>
  </ul>
</template>

<style lang="scss">
.step-navigation {
  $this: 'step-navigation';

  @include reset-list;

  display: flex;

  &__item {
    display: flex;
    align-items: center;
    flex-grow: 1;

    &:last-child {
      flex-grow: 0;
    }

    &--active {
      .#{$this}__check {
        border-color: $color-secondary-800;
        color: $color-secondary-800;
      }

      .#{$this}__label {
        color: $color-new-black-900;
      }
    }

    &--passed {
      .#{$this}__check {
        border-color: $color-secondary-800;
        background-color: $color-secondary-800;
        color: $color-white;
      }

      .#{$this}__label {
        color: $color-new-black-900;
      }

      .#{$this}__line {
        background-color: $color-secondary-800;
      }
    }
  }

  &__link {
    @include reset-btn;

    text-decoration: none;
    display: flex;
    flex-direction: column;
    column-gap: 12px;
    align-items: center;
    padding: 0 24px;

    &[hidden] {
      display: none;
    }

    @include media('md') {
      flex-direction: row;
      padding: 0;
    }
  }

  &__check {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid $color-new-black-400;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-new-black-400;
    margin-bottom: 4px;

    @include media('md') {
      width: 32px;
      height: 32px;
      margin-bottom: 0;
    }
  }

  &__label {
    color: $color-new-black-400;
    font-size: 12px;
    line-height: 18px;

    @include media('md') {
      font-size: 16px;
      line-height: 28px;
    }

    &--pc {
      display: none;

      @include media('md') {
        display: block;
      }
    }

    &--mob {
      display: block;

      @include media('md') {
        display: none;
      }
    }
  }

  &__line {
    height: 1px;
    background-color: $color-dark-blue-300;
    flex-grow: 1;

    @include media('md') {
      margin: 0 16px;
    }
  }
}
</style>
